import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, useParams } from 'react-router-dom';
import { truckService } from '../api/api';
import { AddInspectionDialog, DefectReportDialog, InspectionReportDialog } from './TruckInspectionLogs';
import { getVehicleCategoryId } from '../utils/vehicleCategories';

const DRIVER_FLEET_GROUPS = [
  '5 Ton Trucks',
  'Trailers',
  'Forklifts & Skid Steers',
  'Vans & Sales Vans',
  'Parts Trucks',
  'Mid-Size Box Trucks',
  'Tractors',
  'Other Vehicles',
];

function driverFleetGroup(truck) {
  const category = getVehicleCategoryId(truck);
  const searchable = [truck?.name, truck?.vehicle_type, truck?.folder, truck?.make, truck?.model].filter(Boolean).join(' ').toLowerCase();
  if (category === 'fiveTonnes') return '5 Ton Trucks';
  if (category === 'trailers') return 'Trailers';
  if (category === 'forklifts' || category === 'skidsteer' || searchable.includes('skid steer') || searchable.includes('skidsteer')) return 'Forklifts & Skid Steers';
  if (category === 'vans' || searchable.includes('sales van')) return 'Vans & Sales Vans';
  if (category === 'partsTruck') return 'Parts Trucks';
  if (category === 'midSizeBox') return 'Mid-Size Box Trucks';
  if (category === 'tractors') return 'Tractors';
  return 'Other Vehicles';
}

function labelType(type) {
  if (type === 'pre_trip') return 'Pre-Trip';
  if (type === 'post_trip') return 'Post-Trip';
  if (type === 'defect_report') return 'Defect Report';
  return 'Inspection';
}

function displayTruckName(truck) {
  const base = truck?.name || [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' ') || `Truck #${truck?.id}`;
  const plate = truck?.license_plate ? ` • ${truck.license_plate}` : '';
  return `${base}${plate}`;
}

function truckSecondaryLabel(truck) {
  const details = [
    [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' '),
    truck?.license_plate ? `Plate: ${truck.license_plate}` : '',
    truck?.vehicle_type || truck?.folder || '',
  ].filter(Boolean);
  return details.join(' • ') || 'No additional vehicle details';
}

export default function DriverCheckIn() {
  const navigate = useNavigate();
  const { type } = useParams();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const inspectionType = type === 'post_trip' ? 'post_trip' : type === 'defect_report' ? 'defect_report' : 'pre_trip';
  const isDefectReport = inspectionType === 'defect_report';
  const steps = useMemo(() => ['Select Type', 'Select Truck', isDefectReport ? 'Report Defect' : 'Complete Inspection'], [isDefectReport]);

  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [truckId, setTruckId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [defectOpen, setDefectOpen] = useState(false);
  const [submittedInspection, setSubmittedInspection] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        setLoading(true);
        setError('');
        const res = await truckService.getAll();
        const data = res.data?.data || res.data || [];
        if (!ignore) setTrucks(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!ignore) setError(e?.response?.data?.error || e?.message || 'Failed to load trucks.');
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, []);

  const selectedTruck = useMemo(() => {
    const id = truckId ? parseInt(String(truckId), 10) : null;
    return trucks.find((t) => t.id === id) || null;
  }, [truckId, trucks]);

  const groupedTrucks = useMemo(() => {
    const groups = new Map(DRIVER_FLEET_GROUPS.map((group) => [group, []]));
    trucks.forEach((truck) => groups.get(driverFleetGroup(truck)).push(truck));
    groups.forEach((items) => items.sort((a, b) => displayTruckName(a).localeCompare(displayTruckName(b))));
    return DRIVER_FLEET_GROUPS.map((label) => ({ label, trucks: groups.get(label) })).filter((group) => group.trucks.length > 0);
  }, [trucks]);

  useEffect(() => {
    setSubmittedInspection(null);
  }, [truckId, inspectionType]);

  const activeStep = truckId ? 2 : 1;

  return (
    <Box sx={{ p: { xs: 1.25, sm: 2, md: 3 } }}>
      <Card className="portal-page-header" sx={{ mb: { xs: 1.5, sm: 2.5 }, borderRadius: { xs: 3, sm: 4 }, boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 900, fontSize: { xs: '1.15rem', sm: '1.35rem' }, lineHeight: 1.2 }}>
                Driver Check-In • {labelType(inspectionType)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Choose a truck, then {isDefectReport ? 'report an observed defect.' : 'complete the inspection.'}
              </Typography>
            </Box>
            <DirectionsCarIcon sx={{ fontSize: { xs: 30, sm: 36 }, color: '#0f5aa6' }} />
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Stepper activeStep={activeStep} alternativeLabel={!isPhone} orientation={isPhone ? 'vertical' : 'horizontal'} sx={{ mb: 2, '& .MuiStepLabel-label': { fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.82rem' } } }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 0.7fr) minmax(320px, 1.3fr)' }, flex: 1 }}>
            <FormControl sx={{ minWidth: 0, '& .MuiInputBase-root': { minHeight: 48 } }}>
              <InputLabel>Inspection Type</InputLabel>
              <Select
                value={inspectionType}
                label="Inspection Type"
                onChange={(e) => navigate(`/drivers/check-in/${e.target.value}`)}
              >
                <MenuItem value="pre_trip">Pre-Trip</MenuItem>
                <MenuItem value="post_trip">Post-Trip</MenuItem>
                <MenuItem value="defect_report">Defect Report</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 0, '& .MuiInputBase-root': { minHeight: 48 } }} disabled={loading}>
              <InputLabel>Which Truck?</InputLabel>
              <Select
                value={truckId}
                label="Which Truck?"
                onChange={(e) => setTruckId(e.target.value)}
                renderValue={(value) => {
                  const truck = trucks.find((item) => String(item.id) === String(value));
                  return truck ? displayTruckName(truck) : 'Select a vehicle';
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 480, width: { xs: 'calc(100vw - 32px)', sm: 520 }, maxWidth: 'calc(100vw - 32px)' },
                  },
                }}
              >
                {groupedTrucks.flatMap((group) => [
                  <ListSubheader key={`group-${group.label}`} sx={{ fontWeight: 900, color: '#001f3f', bgcolor: '#dfeefb', lineHeight: '42px', fontSize: '0.82rem', borderBottom: '1px solid #c8dceb' }}>
                    {group.label} ({group.trucks.length})
                  </ListSubheader>,
                  ...group.trucks.map((truck) => (
                    <MenuItem key={truck.id} value={String(truck.id)} sx={{ px: 2.5, py: 1.25, minHeight: 62, whiteSpace: 'normal', borderBottom: '1px solid #edf2f7', alignItems: 'flex-start' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: '#001f3f', lineHeight: 1.25 }}>
                          {truck.name || `Vehicle #${truck.id}`}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.4, color: '#536578', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                          {truckSecondaryLabel(truck)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: isDefectReport ? '1fr' : '1fr 1fr', md: 'auto auto' }, width: { xs: '100%', md: 'auto' } }}>
            <Button
              fullWidth={isPhone}
              variant="contained"
              startIcon={isDefectReport ? <WarningAmberIcon /> : <PlayArrowIcon />}
              color={isDefectReport ? 'warning' : 'primary'}
              disabled={!truckId}
              onClick={() => (isDefectReport ? setDefectOpen(true) : setDialogOpen(true))}
              sx={{ borderRadius: 2, minHeight: 48, minWidth: { md: 160 } }}
            >
              {isDefectReport ? 'Report Defect' : `Start ${labelType(inspectionType)}`}
            </Button>
            {!isDefectReport && (
              <Button
                fullWidth={isPhone}
                variant="outlined"
                color="warning"
                startIcon={<WarningAmberIcon />}
                disabled={!truckId}
                onClick={() => setDefectOpen(true)}
                sx={{ borderRadius: 2, minHeight: 48, minWidth: { md: 150 } }}
              >
                Report Defect
              </Button>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {!error && trucks.length === 0 && !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No trucks found. Add a truck first, then try again.
          </Alert>
        )}
      </Paper>

      {submittedInspection && (
        <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, borderRadius: 3, border: '1px solid #c8e6c9', backgroundColor: '#f1f8e9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontWeight: 900, color: '#1b5e20' }}>
                Submission saved
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {labelType(submittedInspection.inspection_type)} for {selectedTruck ? displayTruckName(selectedTruck) : 'selected truck'}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'auto auto' }, width: { xs: '100%', sm: 'auto' } }}>
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setReportOpen(true)} sx={{ borderRadius: 2, minHeight: 44 }}>
                View Submission
              </Button>
              <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditOpen(true)} sx={{ borderRadius: 2, minHeight: 44 }}>
                Edit Submission
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <AddInspectionDialog
        open={dialogOpen && !isDefectReport}
        onClose={() => setDialogOpen(false)}
        truckId={truckId ? parseInt(String(truckId), 10) : null}
        defaultInspectionType={inspectionType}
        lockInspectionType
        hideTypeSelector
        submitLabel="Submit Inspection"
        onCreated={(inspection) => {
          setDialogOpen(false);
          setSubmittedInspection(inspection);
          setToast({ open: true, severity: 'success', message: `${labelType(inspectionType)} inspection saved for ${selectedTruck ? (selectedTruck.name || `Truck #${selectedTruck.id}`) : 'truck'}.` });
        }}
      />

      <AddInspectionDialog
        open={editOpen && submittedInspection?.inspection_type !== 'defect_report'}
        onClose={() => setEditOpen(false)}
        truckId={truckId ? parseInt(String(truckId), 10) : null}
        existingInspection={submittedInspection}
        defaultInspectionType={submittedInspection?.inspection_type || inspectionType}
        lockInspectionType
        hideTypeSelector
        submitLabel="Save Changes"
        onUpdated={(inspection) => {
          setEditOpen(false);
          setSubmittedInspection(inspection);
          setToast({ open: true, severity: 'success', message: 'Inspection submission updated.' });
        }}
      />

      <InspectionReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        inspection={submittedInspection}
        onEdit={() => {
          setReportOpen(false);
          setEditOpen(true);
        }}
      />

      <DefectReportDialog
        open={defectOpen || (editOpen && submittedInspection?.inspection_type === 'defect_report')}
        onClose={() => { setDefectOpen(false); setEditOpen(false); }}
        truckId={truckId ? parseInt(String(truckId), 10) : null}
        existingInspection={editOpen && submittedInspection?.inspection_type === 'defect_report' ? submittedInspection : null}
        onSaved={(inspection) => {
          setDefectOpen(false);
          setEditOpen(false);
          setSubmittedInspection(inspection);
          setToast({ open: true, severity: 'success', message: editOpen ? 'Defect report updated.' : 'Defect report saved.' });
        }}
      />

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
